import { useRef, useState, ReactNode } from 'react';
import { Popover, Box, Divider, useTheme, IOption, Icon } from '../../base';
import {
    CheckboxIcon,
    CheckboxCheckedIcon,
} from '../../theme/overrides/CustomIcons';
import {
    StyledTrigger,
    StyledTriggerText,
    StyledOptionRow,
    StyledOptionLabel,
} from './SelectDropdown.styles';

interface ISelectDropdownProps {
    options: IOption[];
    selected: IOption | null;
    onChange: (option: IOption | null) => void;
    placeholder: string;
    keepOpenValues?: string[];
    renderExtra?: () => ReactNode;
    multiSelect?: boolean;
    selectedMulti?: IOption[];
    onMultiChange?: (options: IOption[]) => void;
    maxSelectable?: number;
    sectionAfterValues?: string[];
}

const SelectDropdown = ({
    options,
    selected,
    onChange,
    placeholder,
    keepOpenValues,
    renderExtra,
    multiSelect,
    selectedMulti = [],
    onMultiChange,
    maxSelectable,
    sectionAfterValues,
}: ISelectDropdownProps) => {
    const theme = useTheme();
    const triggerRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);

    const isAtCap =
        !!multiSelect &&
        maxSelectable !== undefined &&
        selectedMulti.length >= maxSelectable;

    const handleOptionClick = (option: IOption) => {
        if (multiSelect && onMultiChange) {
            const isAlreadySelected = selectedMulti.some(
                (o) => o.value === option.value
            );
            if (!isAlreadySelected && isAtCap) return;
            if (isAlreadySelected) {
                onMultiChange(
                    selectedMulti.filter((o) => o.value !== option.value)
                );
            } else {
                onMultiChange([...selectedMulti, option]);
            }
            return;
        }
        const isSelected = selected?.value === option.value;
        onChange(isSelected ? null : option);
        const shouldKeepOpen = keepOpenValues?.includes(option.value);
        if (!shouldKeepOpen) {
            setOpen(false);
        }
    };

    const displayText = multiSelect
        ? selectedMulti.length > 0
            ? selectedMulti.map((o) => o.label).join(', ')
            : placeholder
        : selected
          ? selected.label
          : placeholder;
    const isPlaceholder = multiSelect ? selectedMulti.length === 0 : !selected;
    const showExtra =
        renderExtra &&
        keepOpenValues &&
        selected &&
        keepOpenValues.includes(selected.value);

    return (
        <>
            <StyledTrigger
                ref={triggerRef}
                onClick={() => setOpen(!open)}
                isopen={open ? 'true' : 'false'}
            >
                <StyledTriggerText
                    variant="body1"
                    isplaceholder={isPlaceholder ? 'true' : 'false'}
                >
                    {displayText}
                </StyledTriggerText>
                <Icon
                    name="KeyboardArrowDownIcon"
                    size={20}
                    sx={{
                        color: theme.palette.primary.main,
                        transition: 'transform 0.2s',
                        transform: open
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                    }}
                />
            </StyledTrigger>

            <Popover
                open={open}
                anchorEl={triggerRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: theme.direction === 'rtl' ? 'right' : 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: theme.direction === 'rtl' ? 'right' : 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: showExtra
                                ? 0
                                : `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
                            backgroundColor: showExtra
                                ? 'transparent'
                                : undefined,
                            boxShadow: 'none',
                            display: showExtra ? 'flex' : undefined,
                            flexDirection: showExtra
                                ? 'row'
                                : undefined,
                            alignItems: showExtra
                                ? 'flex-start'
                                : undefined,
                            gap: showExtra ? 1 : undefined,
                            overflow: 'visible',
                            border: 'none',
                            minWidth: showExtra
                                ? undefined
                                : triggerRef.current?.offsetWidth,
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderTop: 'none',
                        backgroundColor:
                            theme.palette.common.white,
                        width: triggerRef.current?.offsetWidth,
                        maxHeight: 300,
                        overflowY: 'auto',
                    }}
                >
                    {options.map((option, index) => {
                        const isSelected = multiSelect
                            ? selectedMulti.some(
                                  (o) => o.value === option.value
                              )
                            : selected?.value === option.value;
                        const isDisabled = isAtCap && !isSelected;
                        return (
                            <Box key={option.value}>
                                <StyledOptionRow
                                    onClick={() => {
                                        if (isDisabled) return;
                                        handleOptionClick(option);
                                    }}
                                    sx={{
                                        opacity: isDisabled ? 0.5 : 1,
                                        pointerEvents: isDisabled
                                            ? 'none'
                                            : 'auto',
                                    }}
                                >
                                    {isSelected ? (
                                        <CheckboxCheckedIcon
                                            sx={{
                                                fontSize: 20,
                                                color: theme.palette
                                                    .primary.main,
                                            }}
                                        />
                                    ) : (
                                        <CheckboxIcon
                                            sx={{
                                                fontSize: 20,
                                                color: theme.palette
                                                    .grey[300],
                                            }}
                                        />
                                    )}
                                    <StyledOptionLabel variant="body2">
                                        {option.label}
                                    </StyledOptionLabel>
                                </StyledOptionRow>
                                {index < options.length - 1 && (
                                    <Divider
                                        sx={
                                            sectionAfterValues?.includes(
                                                option.value
                                            )
                                                ? {
                                                      borderBottomWidth: 2,
                                                      borderColor:
                                                          theme.palette
                                                              .grey[400],
                                                  }
                                                : undefined
                                        }
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {showExtra && renderExtra?.()}
            </Popover>
        </>
    );
};

export default SelectDropdown;
